/**
 * 双盲承诺 / 揭示面板（Commit-Reveal 协议的教师上帝视角）。
 *
 * - commitments：pairId → 双方承诺 hash 缩写 + 承诺时间（只公开 hash，不公开底牌）；
 * - reveals：已揭示的配对，展示卡片类型/标题与校验状态；
 * - 徽章：未开始 / 已承诺 / 已揭示。
 */
import { SIDES, sideLabel, type Side } from '@zongheng/core-schema';
import { useArena } from '../../state/ArenaContext.tsx';
import { Badge, EmptyState, cx } from '../ui/primitives.tsx';
import {
  describeRevealVerification,
  formatTimeOfDay,
  shortHash,
  summarizeRevealedCard,
} from '../../export/report.ts';

function verificationTone(verified: boolean | null): 'pass' | 'violation' | 'warning' | 'neutral' {
  if (verified === true) return 'pass';
  if (verified === false) return 'violation';
  return 'warning';
}

export function CommitmentPanel() {
  const { state } = useArena();
  const pairIds = [...new Set(Object.keys(state.commitments))].sort();
  const revealedPairs = new Set(state.reveals.map((reveal) => reveal.pairId));

  return (
    <section className="rounded-xl border border-double-blind/30 bg-bg-surface/80 backdrop-blur">
      <header className="flex items-start justify-between gap-2 border-b border-white/5 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold tracking-wide text-slate-100">双盲承诺 / 揭示</h2>
          <p className="mt-0.5 text-xs text-slate-400">
            Commit-Reveal · SHA-256(Card ‖ Salt) 只公开哈希，揭示后广播明文
          </p>
        </div>
        <div className="flex gap-1">
          <Badge tone="blind">已承诺 {pairIds.length}</Badge>
          <Badge tone="aff">已揭示 {state.reveals.length}</Badge>
        </div>
      </header>
      <div className="space-y-3 p-3">
        {pairIds.length === 0 ? (
          <EmptyState
            title="尚未开始双盲发牌"
            detail="比赛开始后，配对双方会在各自私有通道收到底牌并广播承诺哈希。"
          />
        ) : (
          <ul className="space-y-2">
            {pairIds.map((pairId) => {
              const pair = state.commitments[pairId] ?? {};
              const revealed = revealedPairs.has(pairId);
              return (
                <li
                  key={pairId}
                  className={cx(
                    'rounded-lg border px-2.5 py-2',
                    revealed
                      ? 'border-assert-pass/30 bg-assert-pass/[0.06]'
                      : 'border-double-blind/25 bg-double-blind/[0.06]',
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="mono text-xs font-semibold text-double-blind">配对 {pairId}</span>
                    <Badge tone={revealed ? 'pass' : 'blind'}>{revealed ? '已揭示' : '已承诺'}</Badge>
                  </div>
                  <div className="mt-1 space-y-1">
                    {SIDES.map((side: Side) => {
                      const commitment = pair[side];
                      if (!commitment) {
                        return (
                          <div
                            key={side}
                            className="flex items-center justify-between text-[11px] text-slate-600"
                          >
                            <span>{sideLabel(side)}</span>
                            <span>未开始</span>
                          </div>
                        );
                      }
                      return (
                        <div
                          key={side}
                          data-testid="commitment-item"
                          className="flex flex-wrap items-center justify-between gap-1 rounded-md bg-black/25 px-2 py-1 text-[11px]"
                        >
                          <span
                            className={cx(
                              'font-medium',
                              side === 'AFF' ? 'text-team-aff' : 'text-team-neg',
                            )}
                          >
                            {sideLabel(side)}承诺
                          </span>
                          <span className="mono text-slate-400" title={commitment.hash}>
                            {shortHash(commitment.hash, 16)}
                          </span>
                          <span className="text-slate-600">{formatTimeOfDay(commitment.createdAt)}</span>
                        </div>
                      );
                    })}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {state.reveals.length > 0 && (
          <div>
            <div className="mb-1 text-[11px] uppercase tracking-wider text-slate-500">
              已揭示底牌（{state.reveals.length}）
            </div>
            <ul className="space-y-1.5">
              {state.reveals.map((reveal) => {
                const summary = summarizeRevealedCard(reveal);
                const commitment = state.commitments[reveal.pairId]?.[reveal.side] ?? null;
                const verification = describeRevealVerification(reveal, commitment);
                return (
                  <li
                    key={`${reveal.pairId}:${reveal.stageIndex}:${reveal.side}`}
                    data-testid="reveal-item"
                    className="rounded-lg border border-white/10 bg-black/20 px-2.5 py-1.5"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-1">
                      <span className="text-[11px] text-slate-300">
                        <span className={reveal.side === 'AFF' ? 'text-team-aff' : 'text-team-neg'}>
                          {sideLabel(reveal.side)}
                        </span>{' '}
                        · 环节 {reveal.stageIndex} · {summary.kindLabel}
                      </span>
                      <Badge tone={verificationTone(verification.verified)}>
                        {verification.status === 'demo' ? '演示·未校验' : verification.label}
                      </Badge>
                    </div>
                    <div className="mt-0.5 truncate text-xs text-slate-200">{summary.title}</div>
                    <div className="mono mt-0.5 truncate text-[10px] text-slate-600" title={reveal.hash}>
                      hash {shortHash(reveal.hash, 20)} · salt {shortHash(reveal.salt, 10)}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
